import { Client, type Room } from "colyseus.js";
import type { MatchState } from "@ludo/engine";
import type { RoomReservation } from "../api/http.js";

const wsUrl = import.meta.env.VITE_WS_URL ?? (typeof window !== "undefined" ? `${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.host}` : "ws://localhost:2567");

export interface RoomLobby {
  roomCode: string | null;
  hostSeat: number;
  started: boolean;
  seats: Array<{ seat: number; displayName: string; status: string; occupied: boolean }>;
}

export class LudoRoomClient {
  private readonly client = new Client(wsUrl);
  private room: Room | null = null;
  private onState: ((state: MatchState) => void) | null = null;
  private onError: ((code: string) => void) | null = null;
  private onLobby: ((lobby: RoomLobby) => void) | null = null;
  private onVoice: ((payload: unknown) => void) | null = null;
  private manualDisconnect = false;
  private reconnecting = false;

  async connect(
    reservation: RoomReservation,
    onState: (state: MatchState) => void,
    onError: (code: string) => void,
    onLobby?: (lobby: RoomLobby) => void,
    onVoice?: (payload: unknown) => void,
  ): Promise<void> {
    this.disconnect();
    this.manualDisconnect = false;
    this.onState = onState;
    this.onError = onError;
    this.onLobby = onLobby ?? null;
    this.onVoice = onVoice ?? null;
    this.bindRoom(await this.client.consumeSeatReservation(reservation));
  }

  roll(): void { this.room?.send("ROLL_DICE"); }
  move(tokenId: string): void { this.room?.send("MOVE_TOKEN", { tokenId }); }
  start(): void { this.room?.send("START_MATCH"); }
  sendVoiceSignal(payload: unknown): void { this.room?.send("VOICE_SIGNAL", payload); }
  disconnect(): void {
    this.manualDisconnect = true;
    this.room?.leave(true);
    this.room = null;
    this.reconnecting = false;
  }

  private bindRoom(room: Room): void {
    this.room = room;
    room.onMessage("STATE_SNAPSHOT", (state: MatchState) => this.onState?.(state));
    if (this.onLobby) room.onMessage("LOBBY", (lobby: RoomLobby) => this.onLobby?.(lobby));
    if (this.onVoice) room.onMessage("VOICE_SIGNAL", (payload: unknown) => this.onVoice?.(payload));
    room.onMessage("ERROR", (message: { code?: unknown }) => this.onError?.(typeof message.code === "string" ? message.code : "room_error"));
    room.onLeave(() => { if (!this.manualDisconnect) void this.reconnect(room); });
  }

  private async reconnect(previousRoom: Room): Promise<void> {
    if (this.reconnecting || this.room !== previousRoom || !previousRoom.reconnectionToken) {
      if (this.room === previousRoom) { this.room = null; this.onError?.("connection_closed"); }
      return;
    }
    this.reconnecting = true;
    this.room = null;
    this.onError?.("reconnecting");
    for (let attempt = 0; attempt < 3 && !this.manualDisconnect; attempt += 1) {
      try {
        const room = await this.client.reconnect(previousRoom.reconnectionToken);
        if (this.manualDisconnect) { room.leave(true); return; }
        this.reconnecting = false;
        this.bindRoom(room);
        this.onError?.("reconnected");
        return;
      } catch {
        await new Promise<void>((resolve) => window.setTimeout(resolve, 900 * (attempt + 1)));
      }
    }
    this.reconnecting = false;
    this.onError?.("connection_closed");
  }
}
